/**
 * Excel Parser Service
 * Parses Excel files and validates data for bulk upload
 * Handles formatted numbers (6,550 → 6550)
 */

import * as XLSX from "xlsx";

class ExcelParserService {
  /**
   * Clean and parse numeric value
   * Handles: "6,550" → 6550, "6550" → 6550, "6,550.50" → 6550.50
   */
  cleanNumber(value) {
    if (value == null || value === "") return null;

    // Remove commas and whitespace, then parse
    const cleaned = String(value).replace(/,/g, "").trim();
    const parsed = parseFloat(cleaned);

    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Parse Excel file and extract data
   */
  async parseExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          const result = {
            sheets: {},
            sheetNames: workbook.SheetNames,
          };

          // Parse each sheet
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              raw: false,
              defval: null,
            });
            result.sheets[sheetName] = jsonData;
          });

          resolve(result);
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Validate and transform Entities data
   */
  validateEntities(data, assetId) {
    const validEntityTypes = [
      "Metric",
      "Project",
      "CSF",
      "Constraint",
      "Initiative",
    ];
    const errors = [];
    const validRecords = [];

    data.forEach((row, index) => {
      const rowNum = index + 2; // Excel rows start at 1, header is row 1
      const rowErrors = [];

      // Required fields
      if (!row.Title || !row.Title.trim()) {
        rowErrors.push(`Row ${rowNum}: Title is required`);
      }

      if (!row.EntityType) {
        rowErrors.push(`Row ${rowNum}: EntityType is required`);
      } else if (!validEntityTypes.includes(row.EntityType)) {
        rowErrors.push(
          `Row ${rowNum}: EntityType must be one of: ${validEntityTypes.join(", ")}`,
        );
      }

      // Validate numeric fields - USE cleanNumber
      const targetValue = this.cleanNumber(row.TargetValue);
      const weight = this.cleanNumber(row.Weight);

      if (row.TargetValue && targetValue === null) {
        rowErrors.push(
          `Row ${rowNum}: TargetValue must be a valid number (use 6550, not 6,550)`,
        );
      }

      if (row.Weight && weight === null) {
        rowErrors.push(
          `Row ${rowNum}: Weight must be a valid number (between 0 and 1)`,
        );
      }

      if (weight !== null && (weight < 0 || weight > 1)) {
        rowErrors.push(`Row ${rowNum}: Weight must be between 0 and 1`);
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      } else {
        validRecords.push({
          Title: row.Title.trim(),
          EntityType: row.EntityType,
          Category: row.Category || "Production",
          AssetIdLookupId: assetId,
          Owner: row.Owner || null,
          Weight: weight,
          Description: row.Description || null,
          TargetValue: targetValue,
          TargetUnit: row.TargetUnit || null,
        });
      }
    });

    return { validRecords, errors };
  }

  /**
   * Validate and transform Signals data
   * NOW: Allows duplicates (they will be updated instead)
   */
  validateSignals(data, entitiesMap) {
    const validSignalTypes = ["Value", "Score", "Status", "Boolean", "Text"];
    const validStatuses = [
      "Met",
      "At Risk",
      "Not Met",
      "On Track",
      "Delayed",
      "Blocked",
      "Completed",
    ];
    const errors = [];
    const validRecords = [];
    const dailySignals = new Map(); // Track for info only

    data.forEach((row, index) => {
      const rowNum = index + 2;
      const rowErrors = [];

      // Required fields
      if (!row.EntityTitle) {
        rowErrors.push(`Row ${rowNum}: EntityTitle is required`);
      } else if (!entitiesMap.has(row.EntityTitle)) {
        rowErrors.push(`Row ${rowNum}: Entity '${row.EntityTitle}' not found`);
      }

      if (!row.SignalType) {
        rowErrors.push(`Row ${rowNum}: SignalType is required`);
      } else if (!validSignalTypes.includes(row.SignalType)) {
        rowErrors.push(
          `Row ${rowNum}: SignalType must be one of: ${validSignalTypes.join(", ")}`,
        );
      }

      if (!row.Timestamp) {
        rowErrors.push(`Row ${rowNum}: Timestamp is required`);
      }

      // Validate based on signal type - USE cleanNumber
      let cleanedNumericValue = null;

      if (row.SignalType === "Value" || row.SignalType === "Score") {
        cleanedNumericValue = this.cleanNumber(row.NumericValue);

        if (cleanedNumericValue === null && row.NumericValue !== 0) {
          rowErrors.push(
            `Row ${rowNum}: NumericValue is required for ${row.SignalType} signals (use 6550, not 6,550)`,
          );
        }

        // Score must be 0-1
        if (row.SignalType === "Score" && cleanedNumericValue !== null) {
          if (cleanedNumericValue < 0 || cleanedNumericValue > 1) {
            rowErrors.push(`Row ${rowNum}: Score must be between 0 and 1`);
          }
        }
      }

      if (row.SignalType === "Status" && row.Status) {
        if (!validStatuses.includes(row.Status)) {
          rowErrors.push(
            `Row ${rowNum}: Status must be one of: ${validStatuses.join(", ")}`,
          );
        }
      }

      // Track duplicates in CURRENT upload for warning (but don't block)
      if (
        rowErrors.length === 0 &&
        row.EntityTitle &&
        row.SignalType &&
        row.Timestamp
      ) {
        const signalDate = new Date(row.Timestamp).toISOString().split("T")[0]; // YYYY-MM-DD
        const signalKey = `${row.EntityTitle}|${row.SignalType}|${signalDate}`;

        if (dailySignals.has(signalKey)) {
          // Just log a warning - don't add to errors
          console.warn(
            `Row ${rowNum}: Duplicate in upload - Entity '${row.EntityTitle}' already has ${row.SignalType} for ${signalDate} at row ${dailySignals.get(signalKey)}. Latest value will be used.`,
          );
        }
        // Always set/overwrite with latest row
        dailySignals.set(signalKey, rowNum);
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      } else {
        const entityId = entitiesMap.get(row.EntityTitle);

        validRecords.push({
          EntityIdLookupId: entityId,
          SignalType: row.SignalType,
          NumericValue: cleanedNumericValue,
          Status: row.Status || null,
          BooleanValue:
            row.BooleanValue === "true" || row.BooleanValue === true || null,
          TextValue: row.TextValue || null,
          Reason: row.Reason || null,
          Timestamp: new Date(row.Timestamp).toISOString(),
        });
      }
    });

    return { validRecords, errors };
  }

  /**
   * Validate and transform Rules data
   */
  validateRules(data, entitiesMap) {
    const validRuleTypes = ["Required", "Contributes", "Blocks"];
    const validImpactUnits = ["Absolute", "Percentage"];
    const errors = [];
    const validRecords = [];

    data.forEach((row, index) => {
      const rowNum = index + 2;
      const rowErrors = [];

      // Required fields
      if (!row.Title) {
        rowErrors.push(`Row ${rowNum}: Title is required`);
      }

      if (!row.SourceEntity) {
        rowErrors.push(`Row ${rowNum}: SourceEntity is required`);
      } else if (!entitiesMap.has(row.SourceEntity)) {
        rowErrors.push(
          `Row ${rowNum}: Source entity '${row.SourceEntity}' not found`,
        );
      }

      if (!row.TargetEntity) {
        rowErrors.push(`Row ${rowNum}: TargetEntity is required`);
      } else if (!entitiesMap.has(row.TargetEntity)) {
        rowErrors.push(
          `Row ${rowNum}: Target entity '${row.TargetEntity}' not found`,
        );
      }

      if (!row.RuleType) {
        rowErrors.push(`Row ${rowNum}: RuleType is required`);
      } else if (!validRuleTypes.includes(row.RuleType)) {
        rowErrors.push(
          `Row ${rowNum}: RuleType must be one of: ${validRuleTypes.join(", ")}`,
        );
      }

      if (!row.ConditionExpression) {
        rowErrors.push(`Row ${rowNum}: ConditionExpression is required`);
      }

      if (row.ImpactUnit && !validImpactUnits.includes(row.ImpactUnit)) {
        rowErrors.push(
          `Row ${rowNum}: ImpactUnit must be one of: ${validImpactUnits.join(", ")}`,
        );
      }

      // Validate numeric fields - USE cleanNumber
      const impactValue = this.cleanNumber(row.ImpactValue);
      const priority = this.cleanNumber(row.Priority);

      if (row.ImpactValue && impactValue === null) {
        rowErrors.push(`Row ${rowNum}: ImpactValue must be a valid number`);
      }

      if (row.Priority && priority === null) {
        rowErrors.push(`Row ${rowNum}: Priority must be a valid number`);
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      } else {
        validRecords.push({
          Title: row.Title.trim(),
          SourceEntityIdLookupId: entitiesMap.get(row.SourceEntity),
          TargetEntityIdLookupId: entitiesMap.get(row.TargetEntity),
          RuleType: row.RuleType,
          ConditionExpression: row.ConditionExpression,
          ImpactValue: impactValue,
          ImpactUnit: row.ImpactUnit || "Absolute",
          Priority: priority !== null ? Math.round(priority) : 10,
          IsActive: row.IsActive === "false" ? false : true,
          Description: row.Description || null,
        });
      }
    });

    return { validRecords, errors };
  }

  /**
   * Generate Excel template for Entities
   */
  generateEntitiesTemplate() {
    const headers = [
      "Title",
      "EntityType",
      "Category",
      "Owner",
      "Weight",
      "Description",
      "TargetValue",
      "TargetUnit",
    ];
    const sampleData = [
      [
        "Crude Oil Production",
        "Metric",
        "Production",
        "John Doe",
        0.3,
        "Daily crude oil production",
        5523,
        "BOPD",
      ],
      [
        "Gas Production",
        "Metric",
        "Production",
        "Jane Smith",
        0.25,
        "Daily gas production",
        12500,
        "MMSCF",
      ],
      [
        "Budget Availability",
        "CSF",
        "Production",
        "Finance Team",
        0.2,
        "Ensure budget is available",
        "",
        "",
      ],
      [
        "Phase 4 Development",
        "Project",
        "Production",
        "Mike Johnson",
        0.3,
        "Complete Phase 4",
        3700,
        "BOPD",
      ],
      [
        "Personnel on Board",
        "Metric",
        "HSE",
        "Safety Officer",
        0.2,
        "Current POB count",
        150,
        "persons",
      ],
      [
        "UA/UC Count",
        "Metric",
        "HSE",
        "Safety Officer",
        0.15,
        "Unsafe acts/conditions",
        0,
        "count",
      ],
      [
        "PTW Active",
        "Metric",
        "HSE",
        "Safety Officer",
        0.1,
        "Active permits to work",
        5,
        "permits",
      ],
      [
        "LTI-Free Man-hours",
        "Metric",
        "HSE",
        "Safety Officer",
        0.3,
        "Lost time injury free hours",
        1000000,
        "hours",
      ],
    ];

    return this.createWorkbook("Entities Template", headers, sampleData);
  }

  /**
   * Generate Excel template for Signals
   */
  generateSignalsTemplate() {
    const headers = [
      "EntityTitle",
      "SignalType",
      "NumericValue",
      "Status",
      "Timestamp",
      "Reason",
    ];
    const sampleData = [
      ["Crude Oil Production", "Value", 5200, "", "2026-02-10", "Pipeline shutdown for maintenance"],
      ["Gas Production", "Value", 11800, "", "2026-02-10", ""],
      ["Budget Availability", "Score", 0.85, "Good", "2026-02-10", ""],
      ["Security Clearance", "Status", "", "Met", "2026-02-10", ""],
      ["Personnel on Board", "Value", 145, "", "2026-02-10", ""],
      ["UA/UC Count", "Value", 2, "", "2026-02-10", "Two unsafe conditions reported near rig 3"],
      ["PTW Active", "Value", 7, "", "2026-02-10", ""],
      ["LTI-Free Man-hours", "Value", 1250000, "", "2026-02-10", ""],
    ];

    return this.createWorkbook("Signals Template", headers, sampleData);
  }

  /**
   * Generate Excel template for Rules
   */
  generateRulesTemplate() {
    const headers = [
      "Title",
      "SourceEntity",
      "TargetEntity",
      "RuleType",
      "ConditionExpression",
      "ImpactValue",
      "ImpactUnit",
      "Priority",
      "Description",
    ];
    const sampleData = [
      [
        "Budget blocks production",
        "Budget Availability",
        "Crude Oil Production",
        "Required",
        'Status == "Met"',
        0,
        "Absolute",
        1,
        "Budget must be available for production",
      ],
      [
        "Security contributes to Phase 4",
        "Security Clearance",
        "Phase 4 Development",
        "Contributes",
        'Status == "Met"',
        500,
        "Absolute",
        5,
        "Security clearance helps Phase 4",
      ],
      [
        "Phase 4 contributes to production",
        "Phase 4 Development",
        "Crude Oil Production",
        "Contributes",
        'Status == "Completed"',
        3700,
        "Absolute",
        3,
        "Phase 4 adds production capacity",
      ],
    ];

    return this.createWorkbook("Rules Template", headers, sampleData);
  }

  /**
   * Create workbook from data
   */
  createWorkbook(sheetName, headers, data) {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Set column widths
    const colWidths = headers.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...data.map((row) => String(row[i] || "").length),
      );
      return { wch: Math.min(maxLen + 2, 30) };
    });
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    return wb;
  }

  /**
   * Download workbook as Excel file
   */
  downloadWorkbook(workbook, filename) {
    XLSX.writeFile(workbook, filename);
  }
}

export default new ExcelParserService();
